import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import AdminLayout from '../../components/AdminLayout';
import StatCards from '../../components/StatCards';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import DeleteSuccessModal from '../../components/DeleteSuccessModal';
import { getAllBanners, createBanner, updateBanner, deleteBanner, getAllCategories, IMAGE_BASE_URL } from '../../services/api';

var emptyForm = {
  title: '',
  subtitle: '',
  buttonText: 'Shop Now',
  redirectId: '',
  isActive: true,
  order: '0',
  imageFile: null,
  imagePreview: '',
};

var fmtDate = function (date) {
  if (!date) return 'â€”';
  try { return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (e) { return date; }
};

var BANNER_IMAGE_BASE_URL = (
  process.env.REACT_APP_IMAGE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  IMAGE_BASE_URL ||
  ''
).replace(/\/+$/, '').replace(/\/api$/, '');

var getBannerImagePath = function (banner) {
  if (!banner || typeof banner !== 'object') return '';
  var image = banner.image || banner.bannerImage || banner.imageUrl || banner.imagePath || banner.path;
  if (image && typeof image === 'object') {
    image = image.url || image.secure_url || image.path || image.filename || image.file || '';
  }
  if (image) return image;
  var nested = banner.data || banner.banner || banner.result;
  return nested && typeof nested === 'object' ? getBannerImagePath(nested) : '';
};

var buildImageUrl = function (imagePath) {
  if (!imagePath || typeof imagePath !== 'string') return '';
  var src = imagePath.trim().replace(/\\/g, '/');
  if (!src) return '';
  if (/^(https?:|blob:|data:)/i.test(src)) return src;
  // The backend may persist an absolute local filesystem path. Convert it to
  // the public uploads path before resolving it against the image host.
  var uploadsIndex = src.toLowerCase().indexOf('/uploads/');
  var path = uploadsIndex >= 0 ? src.slice(uploadsIndex + 1) : src.replace(/^\/+/, '');
  if (/^[a-z]:\//i.test(path) || path.indexOf('/') < 0 && path.indexOf('.') < 0) return '';
  return BANNER_IMAGE_BASE_URL ? BANNER_IMAGE_BASE_URL + '/' + path : '/' + path;
};

var withImageUrl = function (banner) {
  if (!banner) return banner;
  var rawImage = getBannerImagePath(banner);
  var url = buildImageUrl(rawImage);
  return Object.assign({}, banner, { imageUrl: url || null, image: rawImage || banner.image });
};

var isActiveStatus = function (item) {
  if (!item) return false;
  if (typeof item.isActive === 'boolean') return item.isActive;
  if (item.isActive !== undefined && item.isActive !== null) {
    var str = String(item.isActive).trim().toLowerCase();
    if (str === 'false' || str === '0' || str === 'inactive') return false;
    if (str === 'true' || str === '1' || str === 'active') return true;
  }
  if (item.status !== undefined && item.status !== null) {
    var st = String(item.status).trim().toLowerCase();
    if (st === 'inactive' || st === 'false' || st === '0') return false;
    if (st === 'active' || st === 'true' || st === '1') return true;
  }
  return true;
};

var isScheduled = function (item) {
  var value = item && (item.startDate || item.startsAt || item.scheduledAt);
  return value ? new Date(value) > new Date() : false;
};

var isExpired = function (item) {
  var value = item && (item.endDate || item.endsAt || item.expiryDate || item.expiresAt);
  return value ? new Date(value) < new Date() : false;
};

var getBannerId = function (banner) {
  if (!banner) return '';
  return banner._id || banner.id || banner.uuid || '';
};

var getRedirectId = function (banner) {
  if (!banner || !banner.redirectId) return '';
  // redirectId may come back populated (object) or as a raw id string
  return (typeof banner.redirectId === 'object') ? (banner.redirectId._id || '') : banner.redirectId;
};

var getRedirectLabel = function (banner) {
  if (!banner || !banner.redirectId) return '—';
  if (typeof banner.redirectId === 'object') {
    return banner.redirectId.name || banner.redirectId.title || banner.redirectId._id || '—';
  }
  return banner.redirectId;
};

var normalizeBanner = function (banner) {
  if (!banner) return banner;
  var isAct = isActiveStatus(banner);
  return withImageUrl(Object.assign({}, banner, {
    _id: banner._id || banner.id,
    order: banner.order != null ? banner.order : 0,
    isActive: isAct,
    status: isAct ? 'active' : 'inactive',
  }));
};

var getBannerListFromResponse = function (res) {
  var payload = res && res.data;

  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.banners)) return payload.banners;
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (payload && Array.isArray(payload.results)) return payload.results;

  if (payload && payload.data && typeof payload.data === 'object') {
    if (Array.isArray(payload.data.banners)) return payload.data.banners;
    if (Array.isArray(payload.data.items)) return payload.data.items;
    if (Array.isArray(payload.data.results)) return payload.data.results;
    if (payload.data._id || payload.data.id) return [payload.data];
  }

  if (payload && (payload._id || payload.id)) return [payload];

  return [];
};

var getBannerStatus = function (banner) {
  if (!banner) return 'Draft';
  return isActiveStatus(banner) ? 'Active' : 'Inactive';
};

var toStartISO     = function (d)   { return d ? new Date(d + 'T00:00:00.000Z').toISOString() : ''; };
var toEndISO       = function (d)   { return d ? new Date(d + 'T23:59:59.999Z').toISOString() : ''; };
var isoToDateInput = function (iso) { if (!iso) return ''; return String(iso).slice(0, 10); };

var isValidTitle = function (title) {
  if (!title.trim()) return false;
  return /^[a-zA-Z0-9\s&,.'!-]*$/.test(title);
};

var getTitleValidationMessage = function (title) {
  if (!title.trim()) return '';
  if (!/^[a-zA-Z0-9\s&,.'!-]*$/.test(title)) return 'Title contains unsupported characters';
  return '';
};



var isValidObjectId = function (id) { return /^[a-f0-9]{24}$/i.test(String(id || '').trim()); };

var e = React.createElement;

/* â”€â”€ Icons â”€â”€ */
var EyeIcon = function () {
  return e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    e('circle', { cx: 12, cy: 12, r: 3 })
  );
};
var PencilIcon = function () {
  return e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    e('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};
var TrashIcon = function () {
  return e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('polyline', { points: '3 6 5 6 21 6' }),
    e('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    e('path', { d: 'M10 11v6M14 11v6' }),
    e('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};
var UploadIcon = function () {
  return e('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('polyline', { points: '16 16 12 12 8 16' }),
    e('line', { x1: 12, y1: 12, x2: 12, y2: 21 }),
    e('path', { d: 'M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3' })
  );
};
var PercentIcon = function () {
  return e('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('line', { x1: 19, y1: 5, x2: 5, y2: 19 }),
    e('circle', { cx: 6.5, cy: 6.5, r: 2.5 }),
    e('circle', { cx: 17.5, cy: 17.5, r: 2.5 })
  );
};
var TagIcon = function () {
  return e('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('path', { d: 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z' }),
    e('line', { x1: 7, y1: 7, x2: 7.01, y2: 7 })
  );
};

var ChevronLeftIcon = function () {
  return e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('polyline', { points: '15 18 9 12 15 6' })
  );
};

var ChevronRightIcon = function () {
  return e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    e('polyline', { points: '9 18 15 12 9 6' })
  );
};

var BANNER_PER_PAGE_OPTIONS = [5, 10, 25, 50];

/* â”€â”€ CSS (same theme/prefix pattern as Offers.jsx, renamed op- -> bn-) â”€â”€ */
var css = [
  "@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');",
  ".bn-page{padding:20px;width:100%;box-sizing:border-box;font-family:'Outfit',sans-serif;color:#0F172A;}",
  ".page-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:12px;}",
  ".page-header-main{display:flex;flex-direction:column;gap:8px;max-width:calc(100% - 160px);}",
  ".page-header-title-row{display:flex;align-items:center;gap:10px;}",
  ".page-header-icon{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:14px;background:#FFF7E8;color:#D99A2B;box-shadow:inset 0 0 0 1px rgba(217,154,43,0.16);animation:iconFloat 5s ease-in-out infinite;}",
  ".page-header-icon svg{width:18px;height:18px;}",
  "@keyframes iconFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-2px);}}",
  ".bn-heading{margin:0;font-size:20px;font-weight:800;color:#0F172A;}",
  ".bn-subtitle{margin:4px 0 0;color:#8B5CF6;font-size:12px;font-weight:700;}",
  ".bn-new-btn{display:inline-flex;align-items:center;gap:6px;border:none;background:linear-gradient(135deg,#6D28D9,#A855F7);color:#fff;border-radius:999px;padding:9px 18px;font-size:13px;font-weight:800;font-family:'Outfit',sans-serif;cursor:pointer;box-shadow:0 6px 18px rgba(109,40,217,0.3);transition:transform .15s;white-space:nowrap;}",
  ".bn-new-btn:hover{transform:translateY(-2px);}",
  ".bn-form-wrap{overflow:hidden;max-height:0;transition:max-height 0.5s ease;margin-bottom:0;}",
  ".bn-form-wrap.open{max-height:6000px;margin-bottom:20px;}",
  ".bn-form-box{background:#20105E;border:1px solid #312E81;border-radius:18px;box-shadow:0 12px 32px rgba(109,40,217,0.15);padding:22px;}",
  ".bn-form-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;}",
  ".bn-form-title{margin:0;font-size:16px;font-weight:800;color:#FFFFFF;}",
  ".bn-form-close{border:none;background:rgba(109,40,217,0.15);color:#A855F7;padding:7px 13px;border-radius:9px;font-size:12px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;}",
  ".bn-group{display:grid;gap:14px;}",
  ".bn-field-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;}",
  "@media(max-width:540px){.bn-field-row{grid-template-columns:1fr !important;}}",
  ".bn-label{display:block;margin-bottom:5px;color:#C4B5FD;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;}",
  ".bn-input,.bn-textarea,.bn-select{width:100%;min-height:38px;border:1px solid #312E81;border-radius:11px;padding:10px 12px;background:#FFFFFF;color:#102a33;font-family:'Outfit',sans-serif;font-size:13px;font-weight:600;outline:none;transition:border-color .14s,box-shadow .14s;box-sizing:border-box;}",
  ".bn-input[type='date']::-webkit-calendar-picker-indicator{filter:invert(1) brightness(2);cursor:pointer;}",
  ".bn-input:focus,.bn-textarea:focus,.bn-select:focus{border-color:#6D28D9;background:#FFFFFF;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}",
  ".bn-textarea{min-height:58px;resize:vertical;}",

  /* Discount type pills */
  ".bn-dtype-switcher{display:flex;gap:8px;flex-wrap:wrap;}",
  ".bn-dtype-pill{flex:1;min-width:120px;display:flex;flex-direction:column;align-items:center;gap:6px;padding:14px 10px;border:2px solid #312E81;border-radius:14px;background:#20105E;cursor:pointer;transition:all 0.18s;font-family:'Outfit',sans-serif;}",
  ".bn-dtype-pill:hover{border-color:#6D28D9;background:rgba(109,40,217,0.2);}",
  ".bn-dtype-pill.dp-active{border-color:#6D28D9;background:rgba(109,40,217,0.2);box-shadow:0 4px 16px rgba(109,40,217,0.25);}",
  ".bn-dtype-ico{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;transition:background 0.18s;}",
  ".dp-pct .bn-dtype-ico{background:rgba(109,40,217,0.15);color:#A855F7;}",
  ".dp-pct.dp-active .bn-dtype-ico{background:#6D28D9;color:#fff;}",
  ".dp-fixed .bn-dtype-ico{background:rgba(168,85,247,0.15);color:#A855F7;}",
  ".dp-fixed.dp-active .bn-dtype-ico{background:#A855F7;color:#fff;}",
  ".bn-dtype-name{font-size:12px;font-weight:800;color:#C4B5FD;}",
  ".bn-dtype-pill.dp-active .bn-dtype-name{color:#FFFFFF;}",

  /* Discount value */
  ".bn-disc-val-row{display:grid;grid-template-columns:1fr auto;gap:10px;align-items:end;}",
  ".bn-disc-suffix{padding:10px 14px;background:rgba(109,40,217,0.15);border:1px solid #312E81;border-radius:11px;font-size:13px;font-weight:800;color:#A855F7;white-space:nowrap;}",

  /* upload */
  ".bn-upload-zone{border:2px dashed #312E81;border-radius:13px;background:rgba(109,40,217,0.1);cursor:pointer;transition:border-color .15s,background .15s;overflow:hidden;position:relative;min-height:96px;display:flex;flex-direction:column;align-items:center;justify-content:center;}",
  ".bn-upload-zone:hover,.bn-upload-zone.drag-over{border-color:#6D28D9;background:rgba(109,40,217,0.15);}",
  ".bn-upload-placeholder{display:flex;flex-direction:column;align-items:center;gap:7px;padding:16px;color:#A855F7;pointer-events:none;}",
  ".bn-upload-placeholder span{font-size:12px;font-weight:700;color:#6D28D9;}",
  ".bn-upload-placeholder small{font-size:11px;color:#C4B5FD;font-weight:500;}",
  ".bn-preview-wrap{position:relative;width:100%;}",
  ".bn-preview-img{width:100%;max-height:170px;min-height:80px;object-fit:cover;display:block;border-radius:11px;background:#1A0B4D;}",
  ".bn-preview-img.img-error{opacity:0.4;filter:grayscale(1);}",
  ".bn-preview-remove{position:absolute;top:8px;right:8px;border:none;background:rgba(109,40,217,0.7);color:#fff;border-radius:7px;width:26px;height:26px;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:3;transition:background .14s;}",
  ".bn-preview-remove:hover{background:#6D28D9;}",
  ".bn-upload-change{position:absolute;bottom:8px;right:8px;border:none;background:rgba(109,40,217,0.85);color:#fff;border-radius:7px;padding:4px 9px;font-size:11px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;z-index:3;}",

  /* submit */
  ".bn-submit-row{display:flex;gap:10px;flex-wrap:wrap;margin:20px -26px -26px;padding:14px 26px 0;background:#20105E;border-top:1px solid rgba(49,46,129,.75);position:sticky;bottom:-26px;z-index:2;}",
  ".bn-submit{border:none;background:linear-gradient(135deg,#6D28D9,#A855F7);color:#fff;font-size:13px;font-weight:800;padding:10px 18px;border-radius:11px;cursor:pointer;font-family:'Outfit',sans-serif;transition:transform .14s;}",
  ".bn-submit:hover:not(:disabled){transform:translateY(-1px);}",
  ".bn-submit:disabled{opacity:0.6;cursor:not-allowed;}",
  ".bn-reset{border:1px solid #312E81;background:transparent;color:#A855F7;font-size:13px;font-weight:700;padding:10px 18px;border-radius:11px;cursor:pointer;font-family:'Outfit',sans-serif;}",
  ".bn-msg{margin-top:4px;font-weight:700;font-size:13px;}",
  ".bn-global-msg{margin-bottom:16px;padding:11px 15px;border-radius:11px;font-size:13px;font-weight:700;}",
  ".bn-global-msg.success{background:rgba(16,185,129,0.15);color:#86EFAC;border:1px solid rgba(16,185,129,0.3);}",
  ".bn-global-msg.error{background:rgba(239,68,68,0.15);color:#FCA5A5;border:1px solid rgba(239,68,68,0.3);}",

  /* table */
  ".bn-table-card{background:rgba(255,255,255,0.98);border:1px solid rgba(62,124,140,0.16);border-radius:16px;box-shadow:0 20px 48px rgba(62,124,140,0.12);padding:18px 20px;width:100%;box-sizing:border-box;overflow:hidden;}",
  ".bn-table-meta{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}",
  ".bn-count{margin:0 0 14px;font-size:13px;color:#111827;font-weight:700;}",
  ".bn-table-wrap{width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;}",
  ".bn-table{width:100%;border-collapse:collapse;background:#ffffff;}",
  ".bn-table th{padding:10px 12px;text-align:left;vertical-align:middle;color:#2d5f6e;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;border-bottom:1px solid rgba(62,124,140,0.16);white-space:nowrap;background:#f8fafc;}",
  ".bn-table td{padding:13px 12px;vertical-align:middle;border-bottom:1px solid rgba(129,140,248,0.12);font-size:13px;color:#111827;}",
  ".bn-table tbody tr:hover{background:rgba(62,124,140,0.06);}",
  ".bn-table tbody tr:last-child td{border-bottom:none;}",
  ".bn-thumb{width:56px;height:36px;border-radius:9px;object-fit:cover;border:1px solid rgba(129,140,248,0.2);background:rgba(99,102,241,0.08);display:block;}",
  ".bn-thumb-placeholder{width:56px;height:36px;border-radius:9px;border:1px dashed rgba(62,124,140,0.22);background:rgba(62,124,140,0.08);display:flex;align-items:center;justify-content:center;color:#2d5f6e;font-size:15px;}",
  ".bn-badge{display:inline-flex;align-items:center;justify-content:center;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;white-space:nowrap;}",
  ".bn-badge.active{background:rgba(52,211,153,0.08);color:#059669;}",
  ".bn-badge.upcoming{background:rgba(245,158,11,0.10);color:#d97706;}",
  ".bn-badge.expired{background:rgba(248,113,113,0.10);color:#dc2626;}",
  ".bn-badge.inactive{background:rgba(248,113,113,0.10);color:#dc2626;}",
  ".actions-cell{ text-align:center; white-space:nowrap; }",
  ".row-actions{display:inline-flex;align-items:center;justify-content:center;gap:8px;}",
  ".row-action-btn{width:32px;height:32px;padding:0;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;border:1px solid transparent;cursor:pointer;transition:background .15s ease,border-color .15s ease,transform .15s ease;}",
  ".row-action-btn svg{width:15px;height:15px;}",
  ".row-action-btn:hover{transform:translateY(-1px);}",
  ".row-action-view{color:#0ea5e9;background:rgba(14,165,233,.12);border-color:rgba(14,165,233,.24);}",
  ".row-action-view:hover{background:rgba(14,165,233,.2);border-color:rgba(14,165,233,.38);}",
  ".row-action-edit{color:#d99a2b;background:rgba(217,154,43,.14);border-color:rgba(217,154,43,.3);}",
  ".row-action-edit:hover{background:rgba(217,154,43,.22);border-color:rgba(217,154,43,.44);}",
  ".row-action-delete{color:#dc2626;background:rgba(220,38,38,.1);border-color:rgba(220,38,38,.22);}",
  ".row-action-delete:hover{background:rgba(220,38,38,.18);border-color:rgba(220,38,38,.36);}",
  ".bn-empty{padding:36px;text-align:center;color:#64748b;font-weight:700;font-size:14px;background:#f8fafc;border:1px dashed rgba(62,124,140,0.18);border-radius:14px;}",
  ".cs-dropdown-list{max-height:220px;overflow-y:auto;scrollbar-width:none;-ms-overflow-style:none;}",
  ".cs-dropdown-list::-webkit-scrollbar{display:none;}",
  ".bn-cs-trigger{width:100%;text-align:left;cursor:pointer;padding:10px 12px;border-radius:11px;border:1px solid rgba(62,124,140,0.28);background:#FFFFFF;color:#102a33;font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;display:flex;justify-content:space-between;align-items:center;box-sizing:border-box;box-shadow:0 1px 2px rgba(15,23,42,0.04);}",
  ".bn-cs-trigger:hover{border-color:#3E7C8C;box-shadow:0 0 0 3px rgba(62,124,140,0.14);}",
  ".bn-loading{padding:36px;text-align:center;color:#2d5f6e;font-weight:700;font-size:14px;}",

  ".bn-page-blur{filter:blur(10px);pointer-events:none;user-select:none;transition:filter 0.2s ease;}",

  ".bn-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(18,10,58,0.85);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);display:flex;align-items:center;justify-content:center;padding:20px;z-index:9999;animation:overlayFadeIn 0.18s ease;}",
  "@keyframes overlayFadeIn{from{opacity:0;}to{opacity:1;}}",
  ".bn-modal{width:min(480px,100%);background:#20105E;border-radius:20px;padding:26px;box-shadow:0 22px 56px rgba(109,40,217,0.3);max-height:calc(100vh - 96px);overflow-y:auto;animation:modalSlideUp 0.2s ease;border:1px solid #312E81;box-sizing:border-box;}",
  "@keyframes modalSlideUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}",
  ".bn-modal-header{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:20px;}",
  ".bn-modal-title{margin:0;font-size:17px;font-weight:800;color:#FFFFFF;}",
  ".bn-modal-close{border:none;background:rgba(109,40,217,0.15);color:#A855F7;width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;font-family:'Outfit',sans-serif;}",
  ".bn-modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;}",
  ".bn-modal-grid.full{grid-template-columns:1fr;}",
  ".bn-modal-lbl{margin:0 0 3px;font-size:11px;color:#C4B5FD;font-weight:700;text-transform:uppercase;letter-spacing:.05em;}",
  ".bn-modal-val{margin:0;color:#FFFFFF;font-size:14px;line-height:1.6;font-weight:600;}",
  ".bn-modal-banner{width:100%;max-height:210px;object-fit:cover;border-radius:13px;margin-bottom:16px;border:1px solid #312E81;background:#1A0B4D;display:block;}",
  "@media(max-width:520px){.bn-modal-grid{grid-template-columns:1fr;}}",
  ".bn-confirm-box{width:min(440px,100%);background:#20105E;border-radius:20px;padding:26px;box-shadow:0 22px 56px rgba(109,40,217,0.3);animation:modalSlideUp 0.2s ease;border:1px solid #312E81;}",
  ".bn-confirm-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:18px;flex-wrap:wrap;}",
  ".bn-confirm-btn{border:none;border-radius:11px;padding:11px 17px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;}",
  ".bn-confirm-btn.cancel{background:rgba(109,40,217,0.15);color:#A855F7;}",
  ".bn-confirm-btn.confirm{background:linear-gradient(135deg,#6D28D9,#A855F7);color:#fff;}",
  ".bn-confirm-btn:disabled{opacity:0.6;cursor:not-allowed;}",

  /* pagination */
  ".bn-pagination{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:18px 4px 4px;margin-top:14px;border-top:1px solid #F1F5F9;flex-wrap:wrap;}",
  ".bn-pagination-left{display:flex;align-items:center;gap:16px;flex-wrap:wrap;}",
  ".bn-pagination-info{font-size:13px;font-weight:600;color:#64748B;}",
  ".bn-pagination-info strong{color:#0F172A;}",
  ".bn-per-page-select{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:#64748B;}",
  ".bn-per-page-select select{padding:6px 10px;border-radius:8px;border:1px solid #CBD5E1;background:#F8FAFC;color:#0F172A;font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;outline:none;transition:border-color 0.15s ease,box-shadow 0.15s ease;}",
  ".bn-per-page-select select:focus{border-color:#0F172A;background:#FFFFFF;box-shadow:0 0 0 3px rgba(15,23,42,0.08);}",
  ".bn-pagination-controls{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}",
  ".bn-page-btn{min-width:36px;height:36px;padding:0 10px;border-radius:8px;border:1px solid #E2E8F0;background:#FFFFFF;color:#334155;font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all 0.15s ease;}",
  ".bn-page-btn:hover:not(:disabled){border-color:#D99A2B;color:#D99A2B;background:#FFFDF9;}",
  ".bn-page-btn.active{background:#0B192C;color:#FFFFFF;border-color:#0B192C;box-shadow:0 2px 6px rgba(11,25,44,0.2);}",
  ".bn-page-btn:disabled{opacity:0.4;cursor:not-allowed;border-color:#E2E8F0;background:#F8FAFC;color:#94A3B8;}",
  "@media(max-width:768px){.bn-pagination{flex-direction:column;align-items:flex-start;gap:12px;}.bn-pagination-controls{width:100%;justify-content:flex-start;}}",
].join('\n');

/* â”€â”€ Custom Select (matches theme, portaled so it escapes clipped containers) â”€â”€ */
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var posState = useState({ top: 0, left: 0, width: 0 }); var pos = posState[0]; var setPos = posState[1];
  var triggerRef = useRef();
  var listRef = useRef();

  var openList = function () {
    if (triggerRef.current) {
      var rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  useEffect(function () {
    if (!open) return;
    var handler = function (ev) {
      if (triggerRef.current && triggerRef.current.contains(ev.target)) return;
      if (listRef.current && listRef.current.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    var reposition = function () {
      if (triggerRef.current) {
        var rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return function () {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  var selected = options.find(function (o) { return o.value === value; });

  return e('div', { style: { position: 'relative' } },
    e('button', {
      type: 'button',
      ref: triggerRef,
      className: 'bn-cs-trigger',
      onClick: function () { if (open) { setOpen(false); } else { openList(); } }
    },
      selected ? selected.label : 'Select',
      e('span', { style: { fontSize: 11, color: '#2d5f6e', marginLeft: 8 } }, '\u25BC')
    ),
    open && createPortal(
      e('div', {
        ref: listRef,
        className: 'cs-dropdown-list',
        style: {
          position: 'fixed', top: pos.top + 'px', left: pos.left + 'px', width: pos.width + 'px',
          background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
          borderRadius: 11, zIndex: 999999,
          boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
        }
      },
        options.map(function (o) {
          var isSel = o.value === value;
          return e('div', {
            key: o.value,
            onClick: function () { onChange(o.value); setOpen(false); },
            style: {
              padding: '10px 12px', fontSize: 13, cursor: 'pointer',
              color: isSel ? '#ffffff' : '#102a33',
              background: isSel ? '#3E7C8C' : '#ffffff',
              fontFamily: "'Outfit',sans-serif",
              transition: 'background 0.15s',
            },
            onMouseEnter: function (ev) { if (!isSel) ev.currentTarget.style.background = '#eef8fa'; },
            onMouseLeave: function (ev) { if (!isSel) ev.currentTarget.style.background = '#ffffff'; }
          }, o.label);
        })
      ),
      document.body
    )
  );
};


/* â”€â”€ Main Component â”€â”€ */
var Banners = function () {
  var bannerState        = useState([]); var banners = bannerState[0]; var setBanners = bannerState[1];
  var formState          = useState(emptyForm); var form = formState[0]; var setForm = formState[1];
  var editingIdState     = useState(null); var editingId = editingIdState[0]; var setEditingId = editingIdState[1];
  var messageState       = useState({ text: '', type: '' }); var message = messageState[0]; var setMessage = messageState[1];
  var viewingBannerState = useState(null); var viewingBanner = viewingBannerState[0]; var setViewingBanner = viewingBannerState[1];
  var showFormState      = useState(false); var showForm = showFormState[0]; var setShowForm = showFormState[1];
  var showEditModalState = useState(false); var showEditModal = showEditModalState[0]; var setShowEditModal = showEditModalState[1];
  var pendingDeleteState = useState(null); var pendingDeleteBanner = pendingDeleteState[0]; var setPendingDeleteBanner = pendingDeleteState[1];
  var deleteSuccessState = useState(null); var deleteSuccess = deleteSuccessState[0]; var setDeleteSuccess = deleteSuccessState[1];
  var loadingState       = useState(false); var loading = loadingState[0]; var setLoading = loadingState[1];
  var submittingState    = useState(false); var submitting = submittingState[0]; var setSubmitting = submittingState[1];
  var deletingState      = useState(false); var deleting = deletingState[0]; var setDeleting = deletingState[1];
  var dragOverState      = useState(false); var dragOver = dragOverState[0]; var setDragOver = dragOverState[1];
  var fieldErrorsState   = useState({}); var fieldErrors = fieldErrorsState[0]; var setFieldErrors = fieldErrorsState[1];
  var categoriesState    = useState([]); var categories = categoriesState[0]; var setCategories = categoriesState[1];
  var statusFilterState  = useState('all'); var statusFilter = statusFilterState[0]; var setStatusFilter = statusFilterState[1];
  var currentPageState   = useState(1); var currentPage = currentPageState[0]; var setCurrentPage = currentPageState[1];
  var perPageState       = useState(10); var itemsPerPage = perPageState[0]; var setItemsPerPage = perPageState[1];
  var fileInputRef       = useRef(null);

  var isAnyModalOpen = !!(showForm || viewingBanner || showEditModal || pendingDeleteBanner || deleteSuccess);

  useEffect(function () {
    var layoutRoots = [
      document.querySelector('.admin-layout'),
      document.querySelector('[class*="sidebar"]'),
      document.querySelector('[class*="AdminLayout"]'),
      document.querySelector('aside'),
      document.querySelector('nav'),
    ].filter(Boolean);

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      layoutRoots.forEach(function (el) { el.classList.add('bn-layout-blur'); });
    } else {
      document.body.style.overflow = '';
      layoutRoots.forEach(function (el) { el.classList.remove('bn-layout-blur'); });
    }

    return function () {
      document.body.style.overflow = '';
      layoutRoots.forEach(function (el) { el.classList.remove('bn-layout-blur'); });
    };
  }, [isAnyModalOpen]);

  var fetchBanners = useCallback(function () {
    setLoading(true);
    getAllBanners()
      .then(function (res) { setBanners(getBannerListFromResponse(res).map(normalizeBanner)); })
      .catch(function (err) {
        var msg = (err.response && err.response.data && err.response.data.message) || 'Failed to load banners.';
        setMessage({ text: msg, type: 'error' });
      })
      .finally(function () { setLoading(false); });
  }, []);

  useEffect(function () { fetchBanners(); }, [fetchBanners]);

  useEffect(function () {
    getAllCategories()
      .then(function (res) {
        var categoryList = res && res.data && (Array.isArray(res.data) ? res.data : res.data.data || res.data.categories || []);
        setCategories(Array.isArray(categoryList) ? categoryList : []);
      })
      .catch(function (err) {
        console.error('Failed to load categories:', err);
        setCategories([]);
      });
  }, []);

  var setField = function (key, val) {
    setForm(function (prev) { return Object.assign({}, prev, { [key]: val }); });

    if (key === 'title') {
      var titleError = getTitleValidationMessage(val);
      setFieldErrors(function (prev) { return Object.assign({}, prev, { title: titleError }); });
    }
  };

  var handleImageFile = function (file) {
    if (!file) return;
    var allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    var extension = (file.name || '').toLowerCase().match(/\.[a-z0-9]+$/);
    var allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    if (!allowed.includes(file.type) && (!extension || !allowedExtensions.includes(extension[0]))) {
      setMessage({ text: 'Please upload a JPG, PNG, WebP, or GIF image.', type: 'error' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) { setMessage({ text: 'Image must be under 2 MB.', type: 'error' }); return; }
    var reader = new FileReader();
    reader.onload = function (ev) {
      setForm(function (prev) { return Object.assign({}, prev, { imageFile: file, imagePreview: ev.target.result }); });
    };
    reader.readAsDataURL(file);
  };

  var handleFileInputChange = function (ev) { handleImageFile(ev.target.files && ev.target.files[0]); ev.target.value = ''; };
  var handleDrop = function (ev) { ev.preventDefault(); setDragOver(false); handleImageFile(ev.dataTransfer.files && ev.dataTransfer.files[0]); };
  var removeImage = function (ev) {
    ev.stopPropagation();
    setForm(function (prev) { return Object.assign({}, prev, { imageFile: null, imagePreview: '' }); });
  };

  var resetForm = function () {
    setForm(emptyForm); setEditingId(null); setMessage({ text: '', type: '' });
    setFieldErrors({}); setShowForm(false); setShowEditModal(false);
  };


  var validate = function () {
  if (!form.title.trim())                                   return 'Please enter a banner title.';
  if (!isValidTitle(form.title))                            return 'Title contains unsupported characters.';
  if (!form.imageFile && !form.imagePreview)                return 'Please upload a banner image.';
  if (!form.redirectId.trim())                              return 'Select a category this banner links to.';
  return null;
};

  var handleSubmit = function (ev) {
    ev.preventDefault();
    var err = validate();
    if (err) { setMessage({ text: err, type: 'error' }); return; }
    setSubmitting(true); setMessage({ text: '', type: '' });

    var fd = new FormData();
    fd.append('title',         form.title.trim().replace(/\s+/g, ' '));
    fd.append('subtitle',      form.subtitle.trim());
    fd.append('buttonText',    form.buttonText.trim() || 'Shop Now');
    fd.append('redirectId',    form.redirectId.trim());
    fd.append('redirectType',  'category');
    fd.append('isActive',      form.isActive);
    fd.append('status',        form.isActive ? 'active' : 'inactive');
    fd.append('order',         Number(form.order) || 0);
    // Field name must match your multer config on the backend route
    // (router.post('/create', upload.single('bannerImage'), createBanner))
    if (form.imageFile) fd.append('bannerImage', form.imageFile);

    var apiCall = editingId ? updateBanner(editingId, fd) : createBanner(fd);
    apiCall
      .then(function (res) {
        var msg = (res.data && res.data.message) || (editingId ? 'Banner updated.' : 'Banner created.');
        setMessage({ text: msg, type: 'success' });
        if (editingId) {
          setBanners(function (prev) {
            return prev.map(function (b) {
              if (getBannerId(b) === editingId) {
                return normalizeBanner(Object.assign({}, b, {
                  isActive: form.isActive,
                  status: form.isActive ? 'active' : 'inactive',
                  title: form.title.trim().replace(/\s+/g, ' '),
                  subtitle: form.subtitle.trim(),
                  buttonText: form.buttonText.trim() || 'Shop Now',
                  redirectId: form.redirectId.trim(),
                  order: Number(form.order) || 0,
                  imageUrl: form.imagePreview || b.imageUrl
                }));
              }
              return b;
            });
          });
        }
        resetForm();
        fetchBanners();
      })
      .catch(function (err) {
        var msg = (err.response && err.response.data && err.response.data.message) || 'Something went wrong.';
        setMessage({ text: msg, type: 'error' });
      })
      .finally(function () { setSubmitting(false); });
  };

  var startEdit = function (banner) {
    var n = normalizeBanner(banner);
    setEditingId(getBannerId(banner));
    setFieldErrors({});
    setForm({
      title:         n.title      || '',
      subtitle:      n.subtitle   || '',
      buttonText:    n.buttonText || 'Shop Now',
      redirectId:    getRedirectId(n),
      isActive:      isActiveStatus(n),
      order:         n.order != null ? String(n.order) : '0',
      imageFile:     null,
      imagePreview:  n.imageUrl || '',
    });
    setMessage({ text: '', type: '' }); setShowForm(false); setShowEditModal(true);
  };

  var confirmDelete = function () {
    if (!pendingDeleteBanner) return;
    setDeleting(true);
    var bannerToDelete = pendingDeleteBanner;
    var bannerId = getBannerId(bannerToDelete);
    deleteBanner(bannerId)
      .then(function (res) {
        setPendingDeleteBanner(null);
        setDeleteSuccess({ id: bannerId, title: bannerToDelete.title });
      })
      .catch(function (err) {
        var msg = (err.response && err.response.data && err.response.data.message) || 'Failed to delete banner.';
        setMessage({ text: msg, type: 'error' });
        setPendingDeleteBanner(null);
      })
      .finally(function () { setDeleting(false); });
  };

  var handleDeleteSuccessClose = function () {
    var deletedId = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setBanners(function (prev) {
        return prev.filter(function (b) { return getBannerId(b) !== deletedId; });
      });
    }
    fetchBanners();
  };

  /* ── Image upload & Stat cards ── */
  var activeBanners = banners.filter(isActiveStatus).length;
  var inactiveBanners = banners.filter(function (b) { return !isActiveStatus(b); }).length;

  var handleStatCardClick = function (filterVal) {
    setStatusFilter(filterVal);
    setCurrentPage(1);
  };

  var bannerStatCards = [
    { title: 'Total Banners', value: banners.length, description: 'Homepage banner records', icon: 'grid', accent: '#d99a2b', active: statusFilter === 'all', onClick: function () { handleStatCardClick('all'); } },
    { title: 'Active Banners', value: activeBanners, description: 'Ready for display', icon: 'check', accent: '#16a34a', active: statusFilter === 'active', onClick: function () { handleStatCardClick('active'); } },
    { title: 'Inactive Banners', value: inactiveBanners, description: 'Hidden banners', icon: 'pause', accent: '#f59e0b', active: statusFilter === 'inactive', onClick: function () { handleStatCardClick('inactive'); } },
  ];

  var filteredBanners = banners.filter(function (banner) {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return isActiveStatus(banner);
    if (statusFilter === 'inactive') return !isActiveStatus(banner);
    return true;
  });

  var totalPages = Math.max(1, Math.ceil(filteredBanners.length / itemsPerPage));
  var safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(function () {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  var startIndex = (safeCurrentPage - 1) * itemsPerPage;
  var endIndex = Math.min(startIndex + itemsPerPage, filteredBanners.length);
  var paginatedBanners = filteredBanners.slice(startIndex, endIndex);

  var getPageNumbers = function () {
    var maxVisible = 5;
    var start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    var end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    var pages = [];
    for (var p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  };

  var renderImageUpload = function () {
    return e('div', null,
      e('label', { className: 'bn-label' }, 'Banner Image *'),
      e('div', {
        className: 'bn-upload-zone' + (dragOver ? ' drag-over' : ''),
        onDragOver: function (ev) { ev.preventDefault(); setDragOver(true); },
        onDragLeave: function () { setDragOver(false); },
        onDrop: handleDrop,
        onClick: function () { if (!form.imagePreview && fileInputRef.current) fileInputRef.current.click(); }
      },
        e('input', { ref: fileInputRef, type: 'file', accept: 'image/jpeg,image/png,image/webp,image/gif', style: { display: 'none' }, onChange: handleFileInputChange }),
        form.imagePreview
          ? e('div', { className: 'bn-preview-wrap' },
              e('img', {
                src: form.imagePreview,
                alt: 'Banner preview',
                className: 'bn-preview-img',
                onError: function (ev) {
                  ev.target.classList.add('img-error');
                  if (form.imagePreview && !/^(blob:|data:)/.test(form.imagePreview)) {
                    var rebuilt = buildImageUrl(form.imagePreview);
                    if (rebuilt && rebuilt !== form.imagePreview) { ev.target.src = rebuilt; ev.target.classList.remove('img-error'); }
                  }
                }
              }),
              e('button', { type: 'button', className: 'bn-preview-remove', onClick: removeImage }, '\u2715'),
              e('button', { type: 'button', className: 'bn-upload-change', onClick: function (ev) { ev.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); } }, '\u2191 Change')
            )
          : e('div', { className: 'bn-upload-placeholder' },
              e(UploadIcon),
              e('span', null, 'Click or drag & drop to upload'),
              e('small', null, 'JPG, PNG, WebP, GIF \u00B7 max 2 MB \u00B7 recommend 1600\u00D7800px')
            )
      )
    );
  };

  /* â”€â”€ All form fields â”€â”€ */
  var renderFormFields = function () {
    return e('div', { className: 'bn-group' },
      renderImageUpload(),
      e('div', null,
        e('label', { className: 'bn-label' }, 'Title *'),
        e('input', {
          className: 'bn-input',
          type: 'text',
          value: form.title,
          placeholder: 'E.g. Premium Whiteboards for Modern Classrooms',
          onChange: function (ev) { setField('title', ev.target.value); }
        }),
        fieldErrors.title ? e('div', { className: 'bn-msg', style: { color: '#FCA5A5' } }, fieldErrors.title) : null
      ),
      e('div', null,
        e('label', { className: 'bn-label' }, 'Subtitle'),
        e('textarea', { className: 'bn-textarea', value: form.subtitle, placeholder: 'Short line shown under the title on the hero slide', onChange: function (ev) { setField('subtitle', ev.target.value); } })
      ),
      e('div', { className: 'bn-field-row' },
        e('div', null,
          e('label', { className: 'bn-label' }, 'Button Text'),
          e('input', { className: 'bn-input', type: 'text', value: form.buttonText, placeholder: 'e.g. Shop Whiteboards', onChange: function (ev) { setField('buttonText', ev.target.value); } })
        ),
        e('div', null,
          e('label', { className: 'bn-label' }, 'Category *'),
          e(CustomSelect, {
            value: form.redirectId,
            onChange: function (val) { setField('redirectId', val); },
            options: [{ value: '', label: '\u2014 Select a category \u2014' }].concat(
              categories.map(function (cat) {
                return { value: cat._id || cat.id, label: cat.name || cat.title || 'Unnamed' };
              })
            )
          })
        )
      ),
      e('div', { className: 'bn-field-row' },
        e('div', null,
          e('label', { className: 'bn-label' }, 'Order'),
          e('input', { className: 'bn-input', type: 'number', min: '0', value: form.order, placeholder: '0', onChange: function (ev) { setField('order', ev.target.value); } })
        ),
        e('div', null,
          e('label', { className: 'bn-label' }, 'Status'),
          e(CustomSelect, {
            value: form.isActive ? 'true' : 'false',
            onChange: function (val) { setField('isActive', val === 'true'); },
            options: [
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]
          })
        )
      ),
      message.text
        ? e('div', { className: 'bn-msg', style: { color: message.type === 'success' ? '#86EFAC' : '#FCA5A5' } }, message.text)
        : null
    );
  };

  /* â”€â”€ Render â”€â”€ */
  return e(AdminLayout, null,
    e('style', null, css + '\n.bn-layout-blur{filter:blur(5px) brightness(0.92);pointer-events:none;user-select:none;transition:filter 0.2s ease;}'),

    e('div', { className: 'bn-page' + (isAnyModalOpen ? ' bn-page-blur' : '') },

      e('div', { className: 'page-header-row' },
        e('div', { className: 'page-header-main' },
          e('div', { className: 'page-header-title-row' },
            e('div', { className: 'page-header-icon' },
              e('svg', { width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' },
                e('rect', { x: 3, y: 5, width: 18, height: 14, rx: 2 }),
                e('path', { d: 'M3 9h18' }),
                e('path', { d: 'M7 13h6' })
              )
            ),
            e('div', null,
              e('h1', { className: 'bn-heading' }, 'Homepage Banners'),
              e('p', { className: 'bn-subtitle' }, 'Manage the hero banners shown on the storefront homepage.')
            )
          )
        ),
        e('button', {
          type: 'button', className: 'bn-new-btn',
          onClick: function () {
            if (showForm) {
              resetForm();
            } else {
              setForm(emptyForm); setEditingId(null); setMessage({ text: '', type: '' });
              setShowEditModal(false); setShowForm(true);
            }
          }
        }, '+ New Banner')
      ),
      e(StatCards, { cards: bannerStatCards }),



      (message.text && !showForm && !showEditModal)
        ? e('div', { className: 'bn-global-msg ' + message.type }, message.text)
        : null,


      e('div', { className: 'bn-table-card' },
        e('div', { className: 'bn-table-meta' },
          e('p', { className: 'bn-count' },
            statusFilter === 'all'
              ? 'All Banners (' + filteredBanners.length + ')'
              : (statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)) + ' Banners (' + filteredBanners.length + ')'
          )
        ),
        loading
          ? e('div', { className: 'bn-loading' }, 'Loading banners\u2026')
          : filteredBanners.length === 0
            ? e('div', { className: 'bn-empty' },
                statusFilter === 'all'
                  ? 'No banners yet. Click + New Banner to add one.'
                  : 'No ' + statusFilter + ' banners found.'
              )
            : e(React.Fragment, null,
                e('div', { className: 'bn-table-wrap' },
                  e('table', { className: 'bn-table' },
                    e('thead', null,
                      e('tr', null,
                        e('th', null, 'S.No'), e('th', null, 'Image'), e('th', null, 'Title'),
                        e('th', null, 'Redirect'),
                        e('th', null, 'Order'),
                        e('th', null, 'Status'), e('th', null, 'Actions')
                      )
                    ),
                    e('tbody', null,
                      paginatedBanners.map(function (banner, index) {
                        var status = getBannerStatus(banner);
                        return e('tr', { key: getBannerId(banner) },
                          e('td', null, startIndex + index + 1),
                          e('td', null,
                            banner.imageUrl
                              ? e('img', {
                                  src: banner.imageUrl, alt: banner.title, className: 'bn-thumb',
                                  onError: function (ev) {
                                    ev.target.style.display = 'none';
                                    var ph = document.createElement('div');
                                    ph.className = 'bn-thumb-placeholder'; ph.textContent = '\uD83D\uDDBC';
                                    ev.target.parentNode.appendChild(ph);
                                  }
                                })
                              : e('div', { className: 'bn-thumb-placeholder' }, '\uD83D\uDDBC')
                          ),
                          e('td', { style: { fontWeight: 600 } }, banner.title),
                          e('td', null,
                            e('div', { style: { fontSize: '11px', color: '#8B5CF6' } }, getRedirectLabel(banner))
                          ),
                          e('td', null, banner.order),
                          e('td', null, e('span', { className: 'bn-badge ' + status.toLowerCase() }, status)),
                          e('td', { className: 'actions-cell' },
                            e('div', { className: 'row-actions' },
                              e('button', { type: 'button', className: 'row-action-btn row-action-view', title: 'View', 'aria-label': 'View', onClick: function (ev) { ev.stopPropagation(); setViewingBanner(banner); } }, e(EyeIcon)),
                              e('button', { type: 'button', className: 'row-action-btn row-action-edit', title: 'Edit', 'aria-label': 'Edit', onClick: function (ev) { ev.stopPropagation(); startEdit(banner); } }, e(PencilIcon)),
                              e('button', { type: 'button', className: 'row-action-btn row-action-delete', title: 'Delete', 'aria-label': 'Delete', onClick: function (ev) { ev.stopPropagation(); setPendingDeleteBanner(banner); } }, e(TrashIcon))
                            )
                          )
                        );
                      })
                    )
                  )
                ),
                filteredBanners.length > 0
                  ? e('div', { className: 'bn-pagination' },
                      e('div', { className: 'bn-pagination-left' },
                        e('span', { className: 'bn-pagination-info' },
                          'Showing ',
                          e('strong', null, (startIndex + 1) + '–' + endIndex),
                          ' of ',
                          e('strong', null, filteredBanners.length),
                          ' banners'
                        ),
                        e('div', { className: 'bn-per-page-select' },
                          e('span', null, 'Rows per page:'),
                          e('select', {
                            value: itemsPerPage,
                            onChange: function (ev) {
                              setItemsPerPage(Number(ev.target.value));
                              setCurrentPage(1);
                            }
                          },
                            BANNER_PER_PAGE_OPTIONS.map(function (opt) {
                              return e('option', { key: opt, value: opt }, opt);
                            })
                          )
                        )
                      ),
                      e('div', { className: 'bn-pagination-controls' },
                        e('button', {
                          type: 'button',
                          className: 'bn-page-btn',
                          disabled: safeCurrentPage <= 1,
                          onClick: function () { setCurrentPage(1); },
                          title: 'First Page'
                        }, '«'),
                        e('button', {
                          type: 'button',
                          className: 'bn-page-btn',
                          disabled: safeCurrentPage <= 1,
                          onClick: function () { setCurrentPage(Math.max(1, safeCurrentPage - 1)); },
                          title: 'Previous Page'
                        }, e(ChevronLeftIcon)),
                        getPageNumbers().map(function (p) {
                          return e('button', {
                            key: p,
                            type: 'button',
                            className: 'bn-page-btn' + (p === safeCurrentPage ? ' active' : ''),
                            onClick: function () { setCurrentPage(p); }
                          }, p);
                        }),
                        e('button', {
                          type: 'button',
                          className: 'bn-page-btn',
                          disabled: safeCurrentPage >= totalPages,
                          onClick: function () { setCurrentPage(Math.min(totalPages, safeCurrentPage + 1)); },
                          title: 'Next Page'
                        }, e(ChevronRightIcon)),
                        e('button', {
                          type: 'button',
                          className: 'bn-page-btn',
                          disabled: safeCurrentPage >= totalPages,
                          onClick: function () { setCurrentPage(totalPages); },
                          title: 'Last Page'
                        }, '»')
                      )
                    )
                  : null
              )
      )
    ),

    /* Add modal */
    showForm
      ? e('div', { className: 'bn-modal-overlay', onClick: function (ev) { if (ev.target === ev.currentTarget) resetForm(); } },
          e('div', { className: 'bn-modal', onClick: function (ev) { ev.stopPropagation(); } },
            e('div', { className: 'bn-modal-header' },
              e('h3', { className: 'bn-modal-title' }, 'Add New Banner'),
              e('button', { type: 'button', className: 'bn-modal-close', onClick: resetForm }, e('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' }, e('line', { x1: 18, y1: 6, x2: 6, y2: 18 }), e('line', { x1: 6, y1: 6, x2: 18, y2: 18 })))
            ),
            e('form', { onSubmit: handleSubmit },
              renderFormFields(),
              e('div', { className: 'bn-submit-row' },
                e('button', { type: 'button', className: 'bn-reset', onClick: resetForm }, 'Cancel'),
                e('button', { type: 'submit', className: 'bn-submit', disabled: submitting }, submitting ? 'Creating...' : 'Create Banner')
              )
            )
          )
        )
      : null,

    /* â”€â”€ View modal â”€â”€ */
    viewingBanner
      ? e('div', { className: 'bn-modal-overlay', onClick: function (ev) { if (ev.target === ev.currentTarget) setViewingBanner(null); } },
          e('div', { className: 'bn-modal' },
            e('div', { className: 'bn-modal-header' },
              e('h3', { className: 'bn-modal-title' }, viewingBanner.title),
              e('button', { type: 'button', className: 'bn-modal-close', onClick: function () { setViewingBanner(null); } }, e('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' }, e('line', { x1: 18, y1: 6, x2: 6, y2: 18 }), e('line', { x1: 6, y1: 6, x2: 18, y2: 18 })))
            ),
            viewingBanner.imageUrl
              ? e('img', { src: viewingBanner.imageUrl, alt: viewingBanner.title, className: 'bn-modal-banner', onError: function (ev) { ev.target.style.display = 'none'; } })
              : null,
            e('div', { className: 'bn-modal-grid' },
              e('div', null, e('p', { className: 'bn-modal-lbl' }, 'Button Text'), e('p', { className: 'bn-modal-val' }, viewingBanner.buttonText || 'Shop Now'))
            ),
            e('div', { className: 'bn-modal-grid' },
              e('div', null, e('p', { className: 'bn-modal-lbl' }, 'Category'), e('p', { className: 'bn-modal-val' }, getRedirectLabel(viewingBanner)))
            ),
            e('div', { className: 'bn-modal-grid' },
              e('div', null, e('p', { className: 'bn-modal-lbl' }, 'Order'), e('p', { className: 'bn-modal-val' }, viewingBanner.order)),
              e('div', null, e('p', { className: 'bn-modal-lbl' }, 'Status'), e('p', { className: 'bn-modal-val' }, getBannerStatus(viewingBanner)))
            ),
            viewingBanner.subtitle
              ? e('div', { className: 'bn-modal-grid full' },
                  e('p', { className: 'bn-modal-lbl' }, 'Subtitle'),
                  e('p', { className: 'bn-modal-val', style: { fontWeight: 400 } }, viewingBanner.subtitle)
                )
              : null
          )
        )
      : null,

    /* â”€â”€ Edit modal â”€â”€ */
    showEditModal
      ? e('div', { className: 'bn-modal-overlay', onClick: function (ev) { if (ev.target === ev.currentTarget) resetForm(); } },
          e('div', { className: 'bn-modal', onClick: function (ev) { ev.stopPropagation(); } },
            e('div', { className: 'bn-modal-header' },
              e('h3', { className: 'bn-modal-title' }, 'Edit Banner'),
              e('button', { type: 'button', className: 'bn-modal-close', onClick: resetForm }, e('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' }, e('line', { x1: 18, y1: 6, x2: 6, y2: 18 }), e('line', { x1: 6, y1: 6, x2: 18, y2: 18 })))
            ),
            e('form', { onSubmit: handleSubmit },
              renderFormFields(),
              e('div', { className: 'bn-submit-row' },
                e('button', { type: 'button', className: 'bn-reset', onClick: resetForm }, 'Cancel'),
                e('button', { type: 'submit', className: 'bn-submit', disabled: submitting }, submitting ? 'Saving...' : 'Save Changes')
              )
            )
          )
        )
      : null,

    /* ── Delete confirm ── */
    pendingDeleteBanner
      ? e(DeleteConfirmModal, {
          isOpen: true,
          title: 'Delete Banner',
          message: 'Are you sure you want to delete this banner?',
          itemName: pendingDeleteBanner.title,
          isDeleting: deleting,
          onCancel: function () { if (!deleting) setPendingDeleteBanner(null); },
          onConfirm: confirmDelete
        })
      : null,

    /* ── Delete success ── */
    deleteSuccess
      ? e(DeleteSuccessModal, {
          isOpen: true,
          message: 'Deleted Successfully',
          subtitle: 'The banner has been deleted successfully.',
          onClose: handleDeleteSuccessClose,
        })
      : null
  );
};

export default Banners;



